import React, { createContext, useContext, useState, useCallback, useEffect } from "react";

const UserContext = createContext();

/**
 * User Context Provider
 * 
 * User object structure:
 * {
 *   id: string,
 *   name: string,
 *   email: string,
 *   phone: string,
 *   role: 'BUSINESSMAN' | 'STAFF',
 *   permissions: {
 *     dashboard: { read: boolean, create: boolean, update: boolean, delete: boolean },
 *     lead_management: { ... },
 *     // ... other modules
 *   }
 * }
 */
export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);

  const TOKEN_KEY = "mellbizz_token";
  const USER_KEY = "mellbizz_user";

  const buildPermissionsMap = useCallback((permissionsArray = []) => {
    return permissionsArray.reduce((acc, p) => {
      if (!p || !p.module || !p.action) return acc;
      const mod = p.module;
      if (!acc[mod]) acc[mod] = { read: false, create: false, update: false, delete: false };
      acc[mod][p.action] = true;
      return acc;
    }, {});
  }, []);

  const transformApiUser = useCallback((apiUser = {}) => {
    const permissionsMap = buildPermissionsMap(apiUser.permissions);
    return {
      id: apiUser._id || apiUser.id,
      name: apiUser.name,
      email: apiUser.email,
      phone: apiUser.phone,
      role: apiUser.role,
      profileImage: apiUser.profileImage,
      companyLogo: apiUser.companyLogo,
      companySeal: apiUser.companySeal,
      gstNumber: apiUser.gstNumber,
      companyName: apiUser.companyName || apiUser.company_name,
      companyShortName: apiUser.companyShortName || apiUser.company_short_name,
      raw: apiUser,
      permissions: permissionsMap,
    };
  }, [buildPermissionsMap]);

  const setUserFromApi = useCallback((loginResponse = {}) => {
    // Accept either the full API response shape, data wrapper, or direct user object
    const data = loginResponse.data || loginResponse;
    const token = data.token || null;
    const apiUser = data.user || data;
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    }
    if (apiUser) {
      const u = transformApiUser(apiUser);
      setUser(u);
      try {
        localStorage.setItem(USER_KEY, JSON.stringify(u.raw || apiUser));
      } catch (e) {
        // ignore
      }
    }
  }, [transformApiUser]);

  const clearUser = useCallback(() => {
    setUser(null);
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    } catch (e) {
      // ignore
    }
  }, []);

  const getToken = useCallback(() => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch (e) {
      return null;
    }
  }, []);

  const isAuthenticated = useCallback(() => !!getToken(), [getToken]);

  const hasPermission = useCallback((moduleName, action) => {
    if (!user) return false;
    if (user.role?.toUpperCase() === "BUSINESSMAN") return true;
    if (!user.permissions) return false;
    const mod = user.permissions[moduleName];
    return !!(mod && mod[action]);
  }, [user]);

  // load from localStorage on mount
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem(USER_KEY);
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        setUser(transformApiUser(parsed));
      }
    } catch (e) {
      // ignore
    }
  }, [transformApiUser]);

  return (
    <UserContext.Provider value={{ user, setUser, setUserFromApi, clearUser, getToken, isAuthenticated, hasPermission }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
